import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, FileType, Search, ShieldCheck, Loader2 } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { api, API, apiError } from "@/lib/api";

export default function Portal() {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDoc(null);
    try {
      const { data } = await api.post("/portal/lookup", { reference: reference.trim(), email: email.trim() });
      setDoc(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="light-scope min-h-screen flex flex-col">
      <Nav theme="light" />
      <div className="flex-1 max-w-[720px] mx-auto w-full px-5 md:px-10 pt-32 md:pt-40 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] transition-colors mb-8">
          <ArrowLeft size={15} /> Back to home
        </Link>
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#B89874]">Client Portal</span>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight text-[#0A0D0B] mt-4 leading-[1.05]">
          Retrieve your signed document
        </h1>
        <p className="mt-5 text-[#58635C] leading-relaxed">
          Enter the reference number (starting with <span className="font-mono-gg text-[#0A0D0B]">GG-</span>) from your confirmation email,
          along with the email address you signed with, to download your copy again.
        </p>

        <form onSubmit={lookup} data-testid="portal-form" className="mt-8 bg-white border border-[#E5E1D8] p-6 space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Reference number</label>
            <input data-testid="portal-reference" value={reference} onChange={(e) => setReference(e.target.value)} required
              placeholder="GG-XXXXXXXX"
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none font-mono-gg tracking-wider uppercase" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Email you signed with</label>
            <input data-testid="portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
          </div>
          <button type="submit" disabled={busy} data-testid="portal-submit"
            className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Find my document
          </button>
        </form>

        {doc && (
          <div data-testid="portal-result" className="mt-6 bg-white border border-[#E5E1D8] p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck size={22} className="text-[#3F6B4E] mt-0.5 shrink-0" />
              <div>
                <div className="font-display text-xl text-[#0A0D0B]">{doc.title}</div>
                <div className="text-sm text-[#58635C] mt-1">
                  Reference <span className="font-mono-gg text-[#0A0D0B]">{doc.reference}</span> · Signed {new Date(doc.created_at).toLocaleDateString("en-NZ")} · Status: {doc.status}
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href={`${API}/submissions/${doc.id}/pdf`} target="_blank" rel="noreferrer" data-testid="portal-download-pdf"
                className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors">
                <Download size={15} /> Download PDF
              </a>
              <a href={`${API}/submissions/${doc.id}/docx`} target="_blank" rel="noreferrer" data-testid="portal-download-docx"
                className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                <FileType size={15} /> Word
              </a>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
